import {
  ArrowBackOutlined,
  DeleteOutlined,
  Inventory2Outlined,
  MoreVertOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";
import { IBrandModel } from "../../../types/equipment";
import AddSetDialog from "./AddSetDialog";
import AnimatedTable, { ColumnDef } from "./AnimatedTable";

interface GearSetItem {
  id: number;
  brand_model: number | null;
  brand_model_name: string;
  quantity: number;
  notes: string;
  category: string;
  available_quantity: number;
  total_quantity: number;
}

interface GearSetDetail {
  id: string;
  name: string;
  description: string;
  status: string;
  itemCount: number;
  updatedAt: string;
  createdAt: string;
  items: Pick<
    GearSetItem,
    "id" | "brand_model" | "brand_model_name" | "quantity" | "notes"
  >[];
}

interface GearSetApiResponse {
  id: number | string;
  name?: string;
  description?: string;
  status?: string;
  items_count?: number;
  updated_at?: string;
  created_at?: string;
  items?: Array<{
    id: number;
    brand_model: number | null;
    brand_model_name?: string;
    quantity?: number;
    notes?: string;
  }>;
}

const fromNow = (dateStr: string) => {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  return `${mins}m ago`;
};

const fmtDate = (dateStr?: string) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const stockColor = (available: number, needed: number) => {
  if (available === 0) return "#e05c5c";
  if (available < needed) return "#ffc107";
  return "#4caf7d";
};

const StatCard = ({
  label,
  value,
  colors,
}: {
  label: string;
  value: React.ReactNode;
  colors: ReturnType<typeof tokens>;
}) => (
  <Box
    sx={{
      backgroundColor: colors.primary[800],
      borderRadius: "14px",
      px: 2.5,
      py: 2,
      minWidth: 130,
      flex: "1 1 130px",
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        color: colors.primary[700],
        mb: 0.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: 16, fontWeight: 700, color: colors.primary[100] }}
    >
      {value}
    </Typography>
  </Box>
);

const ROWS_PER_PAGE = 10;

export default function ViewGearSetPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [gearSet, setGearSet] = useState<GearSetDetail | null>(null);
  const [brandModelList, setBrandModelList] = useState<IBrandModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const fetchGearSet = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiService.get<GearSetApiResponse>(
        `/api/equipments/gearsets/${id}/`,
      );
      const raw = res.data;
      setGearSet({
        id: raw.id.toString(),
        name: raw.name || "",
        description: raw.description || "",
        status: raw.status || "available",
        itemCount: raw.items_count ?? raw.items?.length ?? 0,
        updatedAt: raw.updated_at || "",
        createdAt: raw.created_at || "",
        items: (raw.items || []).map((i) => ({
          id: i.id,
          brand_model: i.brand_model ?? null,
          brand_model_name: i.brand_model_name || "",
          quantity: i.quantity ?? 1,
          notes: i.notes || "",
        })),
      });
    } catch {
      setError("Failed to load gear set. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchBrandModels = useCallback(async () => {
    try {
      const res = await apiService.get<{ results: IBrandModel[] }>(
        "/api/equipments/brand-model/",
      );
      setBrandModelList(res.data.results);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchGearSet();
      fetchBrandModels();
    }
  }, [id, fetchGearSet, fetchBrandModels]);

  const enrichedItems: GearSetItem[] = (gearSet?.items || []).map((item) => {
    const bm = brandModelList.find((b) => b.id === item.brand_model);
    return {
      ...item,
      brand_model_name: bm
        ? `${bm.brand_name || ""} ${bm.model || ""}`.trim()
        : item.brand_model_name || "Unnamed Gear",
      category:
        bm?.category && typeof bm.category === "object"
          ? (bm.category as { name: string }).name
          : "—",
      available_quantity: bm?.available_quantity ?? 0,
      total_quantity: bm?.brand_model_quantity ?? 0,
    };
  });

  const totalPages = Math.ceil(enrichedItems.length / ROWS_PER_PAGE);
  const paginatedItems = enrichedItems.slice(
    page * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE + ROWS_PER_PAGE,
  );

  const totalQty = enrichedItems.reduce((acc, i) => acc + i.quantity, 0);
  const shortageCount = enrichedItems.filter(
    (i) => i.available_quantity < i.quantity,
  ).length;

  const handleDelete = async () => {
    if (!gearSet) return;
    if (
      !window.confirm(
        `Delete gear set "${gearSet.name}"? This cannot be undone.`,
      )
    )
      return;
    setDeleting(true);
    try {
      await apiService.delete(`/api/equipments/gearsets/${gearSet.id}/`);
      navigate("/gear-management/sets");
    } catch {
      setError("Failed to delete gear set.");
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnDef<GearSetItem>[] = [
    {
      key: "name",
      label: "Name",
      render: (item) => (
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
          noWrap
        >
          {item.brand_model_name}
        </Typography>
      ),
    },
    {
      key: "category",
      label: "Category",
      render: (item) => (
        <Typography
          sx={{ fontSize: 13, color: theme.palette.text.secondary }}
          noWrap
        >
          {item.category}
        </Typography>
      ),
    },
    {
      key: "stock",
      label: "In Stock",
      render: (item) => {
        const color = stockColor(item.available_quantity, item.quantity);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 13, color, fontWeight: 500 }}>
              {item.available_quantity} / {item.total_quantity} available
            </Typography>
          </Box>
        );
      },
    },
    {
      key: "notes",
      label: "Notes",
      render: (item) => (
        <Typography
          sx={{
            fontSize: 12,
            color: theme.palette.text.secondary,
            fontStyle: item.notes ? "normal" : "italic",
          }}
          noWrap
        >
          {item.notes || "—"}
        </Typography>
      ),
    },
    {
      key: "quantity",
      label: "Qty needed",
      align: "center",
      render: (item) => {
        return (
          <Typography
            sx={{ fontSize: 13, color: theme.palette.text.secondary }}
            noWrap
          >
            {item.quantity}
          </Typography>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress sx={{ color: colors.primary[400] }} />
      </Box>
    );
  }

  if (!gearSet) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="text.primary">
          Gear set not found.
        </Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Top nav */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ bgcolor: colors.primary[800] }}
          >
            <ArrowBackOutlined />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              fontWeight="bold"
              color={theme.palette.text.primary}
            >
              {gearSet.name}
            </Typography>
            <Typography variant="body2" color={theme.palette.text.secondary}>
              Gear Set · updated {fromNow(gearSet.updatedAt)}
            </Typography>
          </Box>

          <Chip
            label={gearSet.status}
            size="small"
            sx={{
              fontSize: 12,
              height: 26,
              fontWeight: 600,
              borderRadius: "16px",
              textTransform: "capitalize",
              backgroundColor: colors.primary[800],
              color: theme.palette.text.primary,
            }}
          />

          <Button
            variant="contained"
            onClick={() => setEditOpen(true)}
            sx={{
              fontSize: 14,
              fontWeight: 500,
              px: 3,
              height: 40,
              borderRadius: "10px",
            }}
          >
            Edit Set
          </Button>

          <Tooltip title="More actions">
            <IconButton
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ bgcolor: colors.primary[800] }}
            >
              <MoreVertOutlined />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            PaperProps={{ sx: { borderRadius: "12px", minWidth: 160 } }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                handleDelete();
              }}
              disabled={deleting}
              sx={{ color: theme.palette.error.main, gap: 1 }}
            >
              <DeleteOutlined fontSize="small" />
              Delete Set
            </MenuItem>
          </Menu>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Shortage banner */}
        {shortageCount > 0 && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: "12px" }}>
            {shortageCount} item{shortageCount > 1 ? "s" : ""} in this set{" "}
            {shortageCount > 1 ? "don't" : "doesn't"} have enough stock to be
            deployed. Add more equipment or reduce the quantity needed.
          </Alert>
        )}

        {/* Hero card */}
        <Box
          sx={{
            backgroundColor: colors.primary[900],
            borderRadius: "16px",
            p: 3,
            mb: 3,
          }}
        >
          {gearSet.description && (
            <Typography
              variant="body1"
              color={colors.primary[700]}
              mb={3}
              sx={{ maxWidth: 600 }}
            >
              {gearSet.description}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <StatCard
              label="Model Types"
              value={enrichedItems.length}
              colors={colors}
            />
            <StatCard
              label="Total Qty Needed"
              value={totalQty}
              colors={colors}
            />
            <StatCard
              label="Shortages"
              value={
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: shortageCount > 0 ? "#ffc107" : "#4caf7d",
                  }}
                >
                  {shortageCount === 0 ? "None ✓" : shortageCount}
                </Typography>
              }
              colors={colors}
            />
            <StatCard
              label="Created"
              value={fmtDate(gearSet.createdAt)}
              colors={colors}
            />
            <StatCard
              label="Last Updated"
              value={fmtDate(gearSet.updatedAt)}
              colors={colors}
            />
          </Box>
        </Box>

        {/* Items table */}
        <Box
          sx={{
            backgroundColor: colors.primary[900],
            borderRadius: "16px",
            p: 3,
            minHeight: 300,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={600}
            color={theme.palette.text.primary}
            mb={2.5}
          >
            Items in this Set
          </Typography>

          {enrichedItems.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Inventory2Outlined
                sx={{ fontSize: 36, color: colors.primary[600], mb: 1 }}
              />
              <Typography color="text.secondary">
                No items in this gear set.
              </Typography>
            </Box>
          ) : (
            <AnimatedTable
              columns={columns}
              rows={paginatedItems}
              getRowId={(item) => item.id}
              onRowClick={(item) =>
                item.brand_model &&
                navigate(`/gear-management/catalog/${item.brand_model}`)
              }
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              emptyMessage="No items found."
            />
          )}
        </Box>
      </motion.div>

      <AddSetDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        isEdit
        initialData={{
          id: gearSet.id,
          name: gearSet.name,
          description: gearSet.description,
          items: gearSet.items.map((i) => ({
            id: i.id,
            brand_model: i.brand_model ?? 0,
            brand_model_name: i.brand_model_name || "",
            quantity: i.quantity,
            notes: i.notes || "",
          })),
        }}
        onSubmit={async (data) => {
          await apiService.put(`/api/equipments/gearsets/${gearSet.id}/`, {
            name: data.name,
            description: data.description,
            items: data.items,
          });
          setEditOpen(false);
          await fetchGearSet();
        }}
        brandModelList={brandModelList}
        preSelectedIds={[]}
      />
    </Box>
  );
}
